import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import type { CallState } from '@entities/call-item';
import ringbackUrl from '@assets/media/ringback.mp3';
import ringtoneUrl from '@assets/media/ringtone.mp3';

import { useDeviceSettings } from './use-device-settings';

export function useCallSounds(callState?: CallState) {
	const { selectedAudioOutput, spkVolume } = useDeviceSettings();

	const ringtoneRef = useRef<HTMLAudioElement | null>(null);
	const ringbackRef = useRef<HTMLAudioElement | null>(null);
	const blockedSoundRef = useRef<HTMLAudioElement | null>(null);

	useEffect(() => {
		const replayBlockedSound = () => {
			const blockedSound = blockedSoundRef.current;
			if (!blockedSound) return;

			blockedSoundRef.current = null;
			blockedSound.play().catch(() => {});
		};

		document.addEventListener('click', replayBlockedSound, true);
		return () => {
			document.removeEventListener('click', replayBlockedSound, true);
		};
	}, []);

	// создаём / кэшируем объекты один раз
	useLayoutEffect(() => {
		ringtoneRef.current ||= new Audio(ringtoneUrl);
		ringbackRef.current ||= new Audio(ringbackUrl);

		// чтобы играло бесконечно
		ringtoneRef.current.loop = true;
		ringbackRef.current.loop = true;
	}, []);

	// применяем громкость и аудиовыход
	useEffect(() => {
		if (!selectedAudioOutput) return;

		const ringtone = ringtoneRef.current;
		const ringback = ringbackRef.current;
		if (!ringtone || !ringback) return;

		ringtone.volume = spkVolume;
		ringback.volume = spkVolume;

		// смена аудиовыхода (только где поддерживается)
		const applySink = async (el: HTMLAudioElement) => {
			if ('setSinkId' in el) {
				try {
					await el.setSinkId(selectedAudioOutput);
				} catch (error) {
					// полезно вывести в консоль, чтобы видеть точную причину
					console.warn('setSinkId failed', error);
				}
			}
		};

		// последовательно, чтобы ловить отдельные ошибки
		Promise.all([applySink(ringtone), applySink(ringback)]).catch(() => {});
	}, [selectedAudioOutput, spkVolume]);

	const stopSounds = useCallback(() => {
		const ringtone = ringtoneRef.current;
		const ringback = ringbackRef.current;
		blockedSoundRef.current = null;

		if (ringtone) {
			ringtone.pause();
			ringtone.currentTime = 0;
		}
		if (ringback) {
			ringback.pause();
			ringback.currentTime = 0;
		}
	}, []);

	// реагируем на изменение статуса
	useEffect(() => {
		const ringtone = ringtoneRef.current!;
		const ringback = ringbackRef.current!;

		// сбрасываем всё
		stopSounds();

		const playSound = (el: HTMLAudioElement) => {
			el.play().catch((error: DOMException) => {
				// Браузер блокирует autoplay до user gesture — это штатно.
				if (error.name === 'NotAllowedError') {
					blockedSoundRef.current = el;
					return;
				}
				console.error('Play call sound failed:', error);
			});
		};

		switch (callState) {
			// входящий: «idle» + есть incomingCall  ➜ звонок у нас
			case 'incoming': {
				playSound(ringtone);
				break;
			}

			// исходящий: ждём ответ ➜ «гудки»
			case 'calling': {
				playSound(ringback);
				break;
			}

			// всё остальное – ничего не играем
			default: {
				break;
			}
		}
	}, [callState, stopSounds]);

	return { stopSounds };
}
